import { rpc } from "./services/rpc";

export class SocialAuthService {
  private static instance: SocialAuthService;

  static getInstance(): SocialAuthService {
    if (!SocialAuthService.instance) {
      SocialAuthService.instance = new SocialAuthService();
    }
    return SocialAuthService.instance;
  }

  private constructor() {
    // Listen for URL changes to handle OAuth returns
    window.addEventListener("focus", this.handleWindowFocus.bind(this));
  }

  async initiateOAuth(provider: "google" | "facebook" | "apple"): Promise<void> {
    try {
      // Call the backend to get OAuth URL
      const result = await rpc("socialAuthInit", { provider });

      // Redirect to OAuth provider (backend will handle callback)
      window.location.href = result.authUrl;
    } catch (error) {
      console.error("OAuth initiation error:", error);
      this.dispatchAuthEvent("error", {
        error: error instanceof Error ? error.message : "Failed to start authentication",
        provider,
      });
      throw error;
    }
  }

  private handleWindowFocus() {
    // Check URL parameters for OAuth success/error indicators
    const urlParams = new URLSearchParams(window.location.search);
    const welcomeParam = urlParams.get("welcome");
    const errorParam = urlParams.get("error");

    if (welcomeParam === "true") {
      // User was redirected back after successful OAuth with new account
      this.dispatchAuthEvent("success", {
        isNewUser: true,
        provider: "unknown", // We don't know which provider from URL
      });

      // Clean up URL
      const cleanUrl = window.location.pathname;
      window.history.replaceState(null, "", cleanUrl);
    } else if (errorParam) {
      // OAuth error occurred
      this.dispatchAuthEvent("error", {
        error: decodeURIComponent(errorParam),
        provider: "unknown",
      });

      // Clean up URL
      const cleanUrl = window.location.pathname;
      window.history.replaceState(null, "", cleanUrl);
    }
  }

  private dispatchAuthEvent(type: "success" | "error", detail: Record<string, unknown>) {
    window.dispatchEvent(
      new CustomEvent(`social-auth-${type}`, {
        detail,
        bubbles: true,
      }),
    );
  }

  // Keep this method for SDK-based authentication if needed
  async authenticateWithToken(provider: "google" | "facebook" | "apple", token: string): Promise<void> {
    try {
      const result = await rpc("socialAuthCallback", {
        provider,
        token,
        state: "direct-token",
        redirectUri: window.location.origin,
      });

      this.dispatchAuthEvent("success", {
        provider,
        user: result.user,
        isNewUser: result.isNewUser,
      });
    } catch (error) {
      console.error("Token authentication error:", error);
      this.dispatchAuthEvent("error", {
        error: error instanceof Error ? error.message : "Authentication failed",
        provider,
      });
    }
  }
}

// Global social auth service instance
export const socialAuthService = SocialAuthService.getInstance();
